import { EntityCategorizationService, type EntityInfo } from '../services/EntityCategorizationService';
import {
    canonicalEntityScreenHash,
    entityScreenHashHasExplicitVariantPrefix,
    parseEntityScreenHash,
    type EntityScreenMode,
    type ParsedEntityScreenHash,
} from '../utils/entityScreenRouteUtils';
import {
    DASHBOARD_ROUTE,
    type AppRoute,
    type ClassesRoute,
    type EntityRoute,
    type GroupsRoute,
    type PluginRoute,
    type RouteSection,
    type UtilityPage,
    type UtilityRoute,
} from './types';

const REDIRECT_MAP: Record<string, string> = {
    // Utility items incorrectly under #other
    'other/searchindex': 'utilities/searchindexes',
    'other/policyruleaudit': 'utilities/policyruleaudit',
    'other/functioncatalog': 'admin/functioncatalog',
    'other/pluginfunction': 'admin/pluginfunctions/invoke',
    // Plugin function redirects
    'utilities/pluginfunctions': 'admin/pluginfunctions/invoke',
    'admin/pluginfunctions': 'admin/pluginfunctions/invoke',
    // Single-segment canonicalization
    'schemabinding': 'meta/schemabinding',
    'testing': 'utilities/testing',
    'jstesting': 'utilities/jstesting',
    'customfilter': 'utilities/customfilter',
    'patchtesting': 'utilities/patchtesting',
    'scimselect': 'utilities/scimselect',
    'speedcomparison': 'utilities/speedcomparison',
    'searchcompare': 'utilities/searchcompare',
    'encdec': 'utilities/encdec',
    'setgen': 'utilities/setgen',
    'policyruleaudit': 'utilities/policyruleaudit',
    'contenttypes': 'utilities/contenttypes',
    'searchindex': 'utilities/searchindexes',
    'usersettings': 'utilities/usersettings',
    'freeedit': 'utilities/freeedit',
};

const UTILITY_NAME_MAP: Record<string, UtilityPage> = {
    'testing': 'testing',
    'jstesting': 'jsTesting',
    'customfilter': 'customFilter',
    'patchtesting': 'patchTesting',
    'scimselect': 'scimSelect',
    'speedcomparison': 'speedComparison',
    'searchcompare': 'searchCompare',
    'encdec': 'encdec',
    'setgen': 'setGen',
    'policyruleaudit': 'policyRuleAudit',
    'contenttypes': 'contentTypes',
    'searchindex': 'searchIndexes',
    'searchindexes': 'searchIndexes',
    'usersettings': 'userSettings',
    'freeedit': 'freeEdit',
};

const UTILITY_PAGES: UtilityPage[] = [
    'testing', 'jsTesting', 'customFilter', 'patchTesting', 'scimSelect',
    'speedComparison', 'searchCompare', 'encdec', 'setGen', 'policyRuleAudit',
    'contentTypes', 'searchIndexes', 'userSettings', 'freeEdit',
];

const ADMIN_REDIRECT_TYPES = new Set(['journalentry', 'modificationerror']);

const KNOWN_TWO_SEGMENT_SECTIONS = new Set([
    'person', 'classes', 'groups', 'utilities', 'admin', 'meta', 'other', 'localities',
]);

const KNOWN_META_TWO_SEGMENT_TYPES = new Set([
    'resourcetypes', 'serviceproviderconfig', 'schemaentity', 'schemaattribute',
]);

function resolveSection(section: string, entityType: string): RouteSection {
    if (section === 'other' && ADMIN_REDIRECT_TYPES.has(entityType.toLowerCase())) {
        return 'admin';
    }
    return section as RouteSection;
}

function normalizeId(value?: string | null): string | null {
    return value && value.trim() ? value : null;
}

function pathIndicatesEdit(parts: string[]): boolean {
    return parts.some((p) => (p || '').toLowerCase() === 'edit');
}

function capitalizeEntityType(entityType: string): string {
    return entityType.charAt(0).toUpperCase() + entityType.slice(1);
}

function localityDisplayType(entityType: string): string {
    return entityType.toLowerCase() === 'responsibilitycenter'
        ? 'ResponsibilityCenter'
        : capitalizeEntityType(entityType);
}

function entityRoute(
    section: RouteSection,
    entityType: string,
    overrides?: Partial<EntityRoute>,
): EntityRoute {
    return {
        type: 'entity',
        section,
        entityType,
        entityId: null,
        mode: null,
        ...overrides,
    };
}

function pluginRoute(sub: PluginRoute['sub'], overrides?: Partial<PluginRoute>): PluginRoute {
    return {
        type: 'plugin',
        sub,
        pluginId: null,
        pivotEntityId: null,
        pivotDetailId: null,
        prepareCatalogId: null,
        prepareDetailId: null,
        entityId: null,
        mode: null,
        ...overrides,
    };
}

function utilityRoute(page: UtilityPage): UtilityRoute {
    return { type: 'utility', page };
}

function parsedEntityScreenToRoute(parsed: ParsedEntityScreenHash): AppRoute {
    const baseEntityFields = {
        entityId: parsed.entityId,
        mode: parsed.mode,
    };

    switch (parsed.section) {
        case 'groups':
            return {
                type: 'groups',
                entityId: parsed.entityId,
                mode: parsed.mode,
            };
        case 'classes':
            return {
                type: 'classes',
                entityId: parsed.entityId,
                mode: parsed.mode,
            };
        case 'person':
            return entityRoute('person', parsed.entityType ?? '', baseEntityFields);
        case 'localities':
            return entityRoute('localities', parsed.entityType ?? '', baseEntityFields);
        case 'admin':
            return entityRoute('admin', parsed.entityType ?? '', baseEntityFields);
        case 'other': {
            const entityType = parsed.entityType ?? '';
            return entityRoute(resolveSection('other', entityType), entityType, baseEntityFields);
        }
        case 'meta':
            return entityRoute('meta', (parsed.entityType ?? '').toLowerCase(), {
                ...baseEntityFields,
                entityType: parsed.entityType ?? '',
            });
        default:
            return DASHBOARD_ROUTE;
    }
}

function entityInfoToRoute(entityInfo: EntityInfo, entityTypePart: string): AppRoute {
    const lowerEntityType = entityTypePart.toLowerCase();

    if (entityInfo.category.type === 'person') {
        return entityRoute('person', entityTypePart);
    }

    if (entityInfo.urlSection === 'localities') {
        return entityRoute('localities', localityDisplayType(entityTypePart));
    }

    if (entityInfo.urlSection === 'admin') {
        return entityRoute('admin', capitalizeEntityType(entityTypePart));
    }

    if (entityInfo.urlSection === 'other') {
        return entityRoute('other', capitalizeEntityType(entityTypePart));
    }

    if (entityInfo.urlSection === 'meta') {
        return entityRoute('meta', capitalizeEntityType(entityTypePart));
    }

    return entityRoute('other', capitalizeEntityType(entityTypePart));
}

function parseSingleSegment(
    destinationLower: string,
): ParseHashResult {
    switch (destinationLower) {
        case 'dashboard':
            return { route: DASHBOARD_ROUTE };
        case 'person':
            return { route: entityRoute('person', '') };
        case 'schemaentity':
            return { route: entityRoute('meta', 'schemaentity') };
        case 'schemaattribute':
            return { route: entityRoute('meta', 'schemaattribute') };
        case 'schemabinding':
            return {
                route: entityRoute('meta', 'schemabinding'),
                redirect: 'meta/schemabinding',
            };
        case 'resourcetypes':
            return { route: { type: 'special', page: 'resourceTypes' } };
        case 'groups':
            return { route: { type: 'groups', entityId: null, mode: null } };
        case 'classes':
            return { route: { type: 'classes', entityId: null, mode: null } };
        case 'schoolmap':
            return { route: { type: 'special', page: 'schoolMap' } };
        case 'serviceproviderconfig':
            return { route: { type: 'special', page: 'serviceProviderConfig' } };
        case 'testing':
            return { route: utilityRoute('testing'), redirect: 'utilities/testing' };
        case 'jstesting':
            return { route: utilityRoute('jsTesting'), redirect: 'utilities/jstesting' };
        case 'customfilter':
            return { route: utilityRoute('customFilter'), redirect: 'utilities/customfilter' };
        case 'patchtesting':
            return { route: utilityRoute('patchTesting'), redirect: 'utilities/patchtesting' };
        case 'scimselect':
            return { route: utilityRoute('scimSelect'), redirect: 'utilities/scimselect' };
        case 'speedcomparison':
            return { route: utilityRoute('speedComparison'), redirect: 'utilities/speedcomparison' };
        case 'setgen':
            return { route: utilityRoute('setGen'), redirect: 'utilities/setgen' };
        case 'policyruleaudit':
            return { route: utilityRoute('policyRuleAudit'), redirect: 'utilities/policyruleaudit' };
        case 'contenttypes':
            return { route: utilityRoute('contentTypes'), redirect: 'utilities/contenttypes' };
        case 'searchindex':
            return { route: utilityRoute('searchIndexes'), redirect: 'utilities/searchindexes' };
        case 'encdec':
            return { route: utilityRoute('encdec'), redirect: 'utilities/encdec' };
        case 'usersettings':
            return { route: utilityRoute('userSettings'), redirect: 'utilities/usersettings' };
        default: {
            if (UTILITY_PAGES.includes(destinationLower as UtilityPage)) {
                const page = destinationLower as UtilityPage;
                return {
                    route: utilityRoute(page),
                    redirect: `utilities/${destinationLower}`,
                };
            }
            return { route: entityRoute('person', destinationLower) };
        }
    }
}

function parseTwoSegment(
    section: string,
    entityType: string,
    pathIndicatesEditMode: boolean,
): ParseHashResult {
    const entityTypeLower = entityType.toLowerCase();

    if (section === 'person') {
        return {
            route: entityRoute('person', entityType, {
                entityId: null,
                mode: null,
            }),
        };
    }

    if (section === 'classes') {
        if (entityTypeLower === 'create') {
            return {
                route: {
                    type: 'classes',
                    entityId: null,
                    mode: 'create',
                } satisfies ClassesRoute,
            };
        }
        return {
            route: {
                type: 'classes',
                entityId: entityType,
                mode: pathIndicatesEditMode ? 'edit' : 'view',
            },
            redirect: pathIndicatesEditMode ? undefined : `classes/view/${entityType}`,
        };
    }

    if (section === 'groups') {
        if (entityTypeLower === 'create') {
            return {
                route: {
                    type: 'groups',
                    entityId: null,
                    mode: 'create',
                } satisfies GroupsRoute,
            };
        }
        return {
            route: {
                type: 'groups',
                entityId: entityType,
                mode: pathIndicatesEditMode ? 'edit' : 'view',
            },
            redirect: pathIndicatesEditMode ? undefined : `groups/view/${entityType}`,
        };
    }

    if (section === 'utilities') {
        if (entityTypeLower === 'pluginfunctions') {
            return {
                route: pluginRoute('invoke'),
                redirect: 'admin/pluginfunctions/invoke',
            };
        }
        const utilityPage = UTILITY_NAME_MAP[entityTypeLower];
        if (utilityPage) {
            return { route: utilityRoute(utilityPage) };
        }
        return { route: utilityRoute(entityTypeLower as UtilityPage) };
    }

    if (section === 'admin') {
        if (entityTypeLower === 'functioncatalog') {
            return { route: entityRoute('admin', 'functioncatalog') };
        }
        if (entityTypeLower === 'pluginfunctions') {
            return {
                route: pluginRoute('invoke'),
                redirect: 'admin/pluginfunctions/invoke',
            };
        }
        return {
            route: entityRoute('admin', entityTypeLower, {
                entityId: null,
                mode: null,
            }),
        };
    }

    if (section === 'meta') {
        if (entityTypeLower === 'resourcetypes') {
            return { route: { type: 'special', page: 'resourceTypes' } };
        }
        if (entityTypeLower === 'serviceproviderconfig') {
            return { route: { type: 'special', page: 'serviceProviderConfig' } };
        }
        if (entityTypeLower === 'schemaentity' || entityTypeLower === 'schemaattribute') {
            return { route: entityRoute('meta', entityTypeLower) };
        }
        return { route: entityRoute('meta', entityTypeLower) };
    }

    if (section === 'other') {
        return {
            route: entityRoute(resolveSection('other', entityType), entityType, {
                entityId: null,
                mode: null,
            }),
        };
    }

    if (section === 'localities') {
        return {
            route: entityRoute('localities', localityDisplayType(entityType), {
                entityId: null,
                mode: null,
            }),
        };
    }

    return {
        route: entityRoute(section as RouteSection, entityType, {
            entityId: null,
            mode: null,
        }),
    };
}

function parseThreeSegment(
    section: string,
    entityType: string,
    entityId: string,
    pathIndicatesEditMode: boolean,
): ParseHashResult {
    const entityTypeLower = entityType.toLowerCase();
    const thirdLower = (entityId || '').toLowerCase();
    const secondLower = (entityType || '').toLowerCase();

    if (section === 'classes' && (secondLower === 'view' || secondLower === 'edit')) {
        return {
            route: {
                type: 'classes',
                entityId,
                mode: pathIndicatesEditMode || secondLower === 'edit' ? 'edit' : 'view',
            },
        };
    }

    if (section === 'person') {
        if (thirdLower === 'create') {
            return {
                route: entityRoute('person', entityType, {
                    entityId: null,
                    mode: 'create',
                }),
            };
        }
        return {
            route: entityRoute('person', entityType, {
                entityId,
                mode: pathIndicatesEditMode ? 'edit' : 'view',
            }),
        };
    }

    if (section === 'groups' && (secondLower === 'view' || secondLower === 'edit')) {
        return {
            route: {
                type: 'groups',
                entityId,
                mode: pathIndicatesEditMode || secondLower === 'edit' ? 'edit' : 'view',
            },
        };
    }

    if (section === 'groups' && secondLower === 'group') {
        return {
            route: {
                type: 'groups',
                entityId,
                mode: pathIndicatesEditMode ? 'edit' : 'view',
            },
            redirect: pathIndicatesEditMode ? undefined : `groups/view/${entityId}`,
        };
    }

    if (section === 'localities') {
        if (thirdLower === 'create') {
            return {
                route: entityRoute('localities', localityDisplayType(entityType), {
                    entityId: null,
                    mode: 'create',
                }),
            };
        }
        return {
            route: entityRoute('localities', localityDisplayType(entityType), {
                entityId,
                mode: pathIndicatesEditMode ? 'edit' : 'view',
            }),
        };
    }

    if (section === 'meta') {
        return {
            route: entityRoute('meta', entityTypeLower, {
                entityId,
                mode: null,
            }),
        };
    }

    if (section === 'admin') {
        if (entityTypeLower === 'functioncatalog') {
            return {
                route: entityRoute('admin', 'functioncatalog', {
                    entityId: thirdLower === 'create' ? null : entityId,
                    mode: null,
                }),
            };
        }
        if (thirdLower === 'create') {
            return {
                route: entityRoute('admin', entityType, {
                    entityId: null,
                    mode: 'create',
                }),
            };
        }
        if (entityTypeLower === 'pluginfunctions') {
            const sub = thirdLower;
            if (sub === 'invoke') {
                return { route: pluginRoute('invoke') };
            }
            if (sub === 'table') {
                return { route: pluginRoute('table') };
            }
            return { route: DASHBOARD_ROUTE };
        }
        return {
            route: entityRoute('admin', entityType, {
                entityId,
                mode: pathIndicatesEditMode ? 'edit' : 'view',
            }),
        };
    }

    if (section === 'other') {
        if (entityTypeLower === 'searchindex') {
            return {
                route: utilityRoute('searchIndexes'),
                redirect: 'utilities/searchindex',
            };
        }
        if (thirdLower === 'create') {
            return {
                route: entityRoute(resolveSection('other', entityType), entityType, {
                    entityId: null,
                    mode: 'create',
                }),
            };
        }
        if (entityTypeLower === 'pluginfunction') {
            return {
                route: pluginRoute('table', { entityId }),
                redirect: `admin/pluginfunctions/table/${encodeURIComponent(entityId)}`,
            };
        }
        return {
            route: entityRoute(resolveSection('other', entityType), entityType, {
                entityId,
                mode: pathIndicatesEditMode ? 'edit' : 'view',
            }),
        };
    }

    return { route: DASHBOARD_ROUTE };
}

function parseFourSegment(
    section: string,
    entityType: string,
    selectedType: string,
    entityId: string,
    pathIndicatesEditMode: boolean,
): ParseHashResult {
    const thirdLower = (selectedType || '').toLowerCase();
    const entityTypeLower = entityType.toLowerCase();

    if ((thirdLower === 'view' || thirdLower === 'edit') && entityId) {
        const entityViewMode: EntityScreenMode = pathIndicatesEditMode || thirdLower === 'edit' ? 'edit' : 'view';

        if (section === 'person') {
            return {
                route: entityRoute('person', entityType, {
                    entityId,
                    mode: entityViewMode,
                }),
            };
        }
        if (section === 'localities') {
            return {
                route: entityRoute('localities', localityDisplayType(entityType), {
                    entityId,
                    mode: entityViewMode,
                }),
            };
        }
        if (section === 'admin' && entityTypeLower !== 'pluginfunctions') {
            return {
                route: entityRoute('admin', entityType, {
                    entityId,
                    mode: entityViewMode,
                }),
            };
        }
        if (section === 'other') {
            return {
                route: entityRoute(resolveSection('other', entityType), entityType, {
                    entityId,
                    mode: entityViewMode,
                }),
            };
        }
        if (section === 'meta' && (entityTypeLower === 'schemaentity' || entityTypeLower === 'schemaattribute')) {
            return {
                route: entityRoute('meta', entityTypeLower, {
                    entityId,
                    mode: entityViewMode,
                }),
            };
        }
    }

    if (thirdLower === 'create') {
        const createRoute = (): EntityRoute => {
            if (section === 'person') {
                return entityRoute('person', entityType, { entityId: null, mode: 'create' });
            }
            if (section === 'localities') {
                return entityRoute('localities', localityDisplayType(entityType), {
                    entityId: null,
                    mode: 'create',
                });
            }
            if (section === 'admin' && entityTypeLower !== 'pluginfunctions') {
                return entityRoute('admin', entityType, { entityId: null, mode: 'create' });
            }
            if (section === 'other') {
                return entityRoute(resolveSection('other', entityType), entityType, {
                    entityId: null,
                    mode: 'create',
                });
            }
            return entityRoute(section as RouteSection, entityType, { entityId: null, mode: 'create' });
        };
        return { route: createRoute() };
    }

    if (section === 'admin' && entityTypeLower === 'pluginfunctions') {
        const sub = thirdLower;
        if (sub === 'invoke') {
            return {
                route: pluginRoute('invoke', { pluginId: normalizeId(entityId) }),
            };
        }
        if (sub === 'table') {
            return {
                route: pluginRoute('table', { entityId: normalizeId(entityId) }),
            };
        }
    }

    if (section === 'meta' && (entityTypeLower === 'schemaentity' || entityTypeLower === 'schemaattribute')) {
        return {
            route: entityRoute('meta', entityTypeLower, {
                entityId: normalizeId(entityId),
                mode: null,
            }),
        };
    }

    return { route: DASHBOARD_ROUTE };
}

function parseFiveSegment(
    section: string,
    entityType: string,
    sub: string,
    idA: string,
    idB: string,
    pathIndicatesEditMode: boolean,
): ParseHashResult {
    const entityTypeLower = entityType.toLowerCase();
    const subLower = sub.toLowerCase();
    const actionLower = (idA || '').toLowerCase();

    if (section === 'admin' && entityTypeLower === 'pluginfunctions' && subLower === 'table') {
        if (actionLower === 'edit' || actionLower === 'view') {
            return {
                route: pluginRoute('table', {
                    entityId: normalizeId(idB),
                    mode: pathIndicatesEditMode || actionLower === 'edit' ? 'edit' : 'view',
                }),
            };
        }
    }

    if (section === 'admin' && entityTypeLower === 'pluginfunctions' && subLower === 'invoke') {
        if (actionLower === 'prepare') {
            return {
                route: pluginRoute('invoke', { prepareCatalogId: normalizeId(idB) }),
            };
        }
        return {
            route: pluginRoute('invoke', {
                pluginId: normalizeId(idA),
                pivotEntityId: normalizeId(idB),
            }),
        };
    }

    return { route: DASHBOARD_ROUTE };
}

function parseSixSegment(
    section: string,
    entityType: string,
    sub: string,
    idA: string,
    idB: string,
    detailId: string,
): ParseHashResult {
    const entityTypeLower = entityType.toLowerCase();
    const subLower = sub.toLowerCase();

    if (section === 'admin' && entityTypeLower === 'pluginfunctions' && subLower === 'invoke') {
        return {
            route: pluginRoute('invoke', {
                pluginId: normalizeId(idA),
                pivotEntityId: normalizeId(idB),
                pivotDetailId: normalizeId(detailId),
            }),
        };
    }

    return { route: DASHBOARD_ROUTE };
}

function parseSevenSegment(
    section: string,
    entityType: string,
    sub: string,
    prepareLiteral: string,
    pluginId: string,
    pivotEntityId: string,
    catalogId: string,
): ParseHashResult {
    const entityTypeLower = entityType.toLowerCase();
    const subLower = sub.toLowerCase();

    if (
        section === 'admin' &&
        entityTypeLower === 'pluginfunctions' &&
        subLower === 'invoke' &&
        prepareLiteral.toLowerCase() === 'prepare'
    ) {
        return {
            route: pluginRoute('invoke', {
                prepareCatalogId: normalizeId(catalogId),
                pluginId: normalizeId(pluginId),
                pivotEntityId: normalizeId(pivotEntityId),
            }),
        };
    }

    return { route: DASHBOARD_ROUTE };
}

function parseEightSegment(
    section: string,
    entityType: string,
    sub: string,
    prepareLiteral: string,
    pluginId: string,
    pivotEntityId: string,
    detailId: string,
    catalogId: string,
): ParseHashResult {
    const entityTypeLower = entityType.toLowerCase();
    const subLower = sub.toLowerCase();

    if (
        section === 'admin' &&
        entityTypeLower === 'pluginfunctions' &&
        subLower === 'invoke' &&
        prepareLiteral.toLowerCase() === 'prepare'
    ) {
        return {
            route: pluginRoute('invoke', {
                prepareCatalogId: normalizeId(catalogId),
                pluginId: normalizeId(pluginId),
                pivotEntityId: normalizeId(pivotEntityId),
                prepareDetailId: normalizeId(detailId),
            }),
        };
    }

    return { route: DASHBOARD_ROUTE };
}

function needsAsyncCategorization(section: string, entityType: string): boolean {
    const entityTypeLower = entityType.toLowerCase();

    if (!KNOWN_TWO_SEGMENT_SECTIONS.has(section)) {
        return true;
    }

    if (section === 'meta' && !KNOWN_META_TWO_SEGMENT_TYPES.has(entityTypeLower)) {
        return true;
    }

    if (section === 'localities') {
        return true;
    }

    if (section === 'other') {
        return true;
    }

    return false;
}

export interface ParseHashResult {
    route: AppRoute;
    /** When set, caller should update window.location.hash to this value */
    redirect?: string;
}

export function parseHash(rawHash: string): ParseHashResult {
    const hash = rawHash.replace(/^#/, '');

    if (!hash || hash === 'dashboard') {
        return { route: DASHBOARD_ROUTE };
    }

    if (/\b(code|id_token|access_token|client_info|session_state|state)=/.test(hash)) {
        return { route: DASHBOARD_ROUTE, redirect: 'dashboard' };
    }

    const [hashWithoutSearch] = hash.split('?');
    const parts = hashWithoutSearch.split('/').filter(Boolean);
    const pathLower = hashWithoutSearch.toLowerCase();

    const redirectTarget = REDIRECT_MAP[pathLower];
    if (redirectTarget) {
        const redirectResult = parseHash('#' + redirectTarget);
        return { ...redirectResult, redirect: redirectTarget };
    }

    const editInPath = pathIndicatesEdit(parts);
    const parsedEntityScreen = parseEntityScreenHash(hashWithoutSearch);
    if (parsedEntityScreen) {
        const route = parsedEntityScreenToRoute(parsedEntityScreen);
        if (entityScreenHashHasExplicitVariantPrefix(hashWithoutSearch)) {
            const searchPart = hash.includes('?') ? hash.slice(hash.indexOf('?')) : '';
            const canonical = canonicalEntityScreenHash(parsedEntityScreen);
            return {
                route,
                redirect: canonical.replace(/^#/, '') + searchPart,
            };
        }
        return { route };
    }

    if (parts.length === 1) {
        return parseSingleSegment(parts[0].toLowerCase());
    }

    if (parts.length === 2) {
        return parseTwoSegment(parts[0].toLowerCase(), parts[1], editInPath);
    }

    if (parts.length === 3) {
        return parseThreeSegment(parts[0].toLowerCase(), parts[1], parts[2], editInPath);
    }

    if (parts.length === 4) {
        return parseFourSegment(parts[0].toLowerCase(), parts[1], parts[2], parts[3], editInPath);
    }

    if (parts.length === 5) {
        return parseFiveSegment(parts[0].toLowerCase(), parts[1], parts[2], parts[3], parts[4], editInPath);
    }

    if (parts.length === 6) {
        return parseSixSegment(parts[0].toLowerCase(), parts[1], parts[2], parts[3], parts[4], parts[5]);
    }

    if (parts.length === 7) {
        return parseSevenSegment(
            parts[0].toLowerCase(),
            parts[1],
            parts[2],
            parts[3],
            parts[4],
            parts[5],
            parts[6],
        );
    }

    if (parts.length === 8) {
        return parseEightSegment(
            parts[0].toLowerCase(),
            parts[1],
            parts[2],
            parts[3],
            parts[4],
            parts[5],
            parts[6],
            parts[7],
        );
    }

    return { route: DASHBOARD_ROUTE };
}

export async function parseHashAsync(rawHash: string): Promise<ParseHashResult> {
    const syncResult = parseHash(rawHash);
    const hash = rawHash.replace(/^#/, '');
    const [hashWithoutSearch] = hash.split('?');
    const parts = hashWithoutSearch.split('/').filter(Boolean);

    if (parts.length !== 2) {
        return syncResult;
    }

    const section = parts[0].toLowerCase();
    const entityType = parts[1];

    if (!needsAsyncCategorization(section, entityType)) {
        return syncResult;
    }

    const categorizationService = EntityCategorizationService.getInstance();
    const path = `${section}/${entityType}`;
    const entityInfo = await categorizationService.getEntityInfoFromRelativePath(path);

    if (!entityInfo) {
        return syncResult;
    }

    return {
        route: entityInfoToRoute(entityInfo, entityType),
        redirect: syncResult.redirect,
    };
}
